import React from 'react'

// Constants
import { routes } from '@sketch/constants'

// Utils
import {
  getWorkspaceUserRolePermissions,
  workspaceSettingsPermissions,
} from 'modules/workspace/utils'

// Local Icons
import { icons } from '../../icons'

// Hooks
import { useDiscover } from 'modules/discover/hooks'

// Compoments
import { LinkItem } from 'modules/projects/components/Item'

// Local Components
import HelpMenu from '../HelpMenu'
import DiscoverNewContentPill from '../DiscoverNewContentPill'
import SettingsMenu from '../SettingsMenu/SettingsMenu'
import { SkeletonSidebarList } from '@sketch/components'

// Styles
import { Wrapper } from './BottomMenu.styles'

// Types
import {
  useGetBillingHistoryCountQuery,
  useGetCustomerProviderQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

interface BottomMenuProps {
  workspace?: WorkspaceMinimalFragment
}

interface DerivedMenusProps {
  workspace: WorkspaceMinimalFragment
}

/**
 *
 * DerivedMenus
 *
 * These menus need some data to be computed and should
 * render a skeleton UI.
 *
 * They were separated from the "BottomMenu" because the "HelpMenu"
 * doesn't need a Skeleton UI and can be rendered straight away
 *
 */
const DerivedMenus: React.FC<DerivedMenusProps> = ({ workspace }) => {
  const { hasNewContent } = useDiscover(workspace.identifier)

  const workspaceId = workspace.identifier
  // Some queries will be skipped if there is no customerId
  // so we default it to an empty string
  const customerId = workspace.customer?.identifier || ''

  const {
    isAdmin,
    canManageBillingSetting,
    isGuest,
  } = getWorkspaceUserRolePermissions(workspace)

  const billingHistoryCount = useGetBillingHistoryCountQuery({
    variables: { customerId },
    skip: !isAdmin || !customerId,
  })

  const customerProvider = useGetCustomerProviderQuery({
    variables: { customerId },
    skip: !(canManageBillingSetting && customerId),
  })

  const loading = customerProvider.loading || billingHistoryCount.loading

  if (loading) {
    return <SkeletonSidebarList />
  }

  const {
    showWorkspaceSettingsBilling: showBilling,
    showWorkspaceSettingsGeneral: showGeneral,
    showDiscover,
  } = workspaceSettingsPermissions({
    workspace,
    customerProvider: customerProvider.data,
    billingHistoryCount: billingHistoryCount.data,
  })

  const canRenderSettingsMenu = !isGuest
  const hasPeopleMenuOnly =
    !showBilling && !showGeneral && canRenderSettingsMenu

  const settingsMenuPermissions = {
    isAdmin,
    showWorkspaceSettingsBilling: showBilling,
    showWorkspaceSettingsGeneral: showGeneral,
  }

  return (
    <>
      {showDiscover && (
        <LinkItem
          icon={icons.CompassIcon}
          text="Discover"
          to={routes.WORKSPACE_DISCOVER.create({ workspaceId })}
          additional={hasNewContent && <DiscoverNewContentPill />}
        />
      )}

      {hasPeopleMenuOnly ? (
        <LinkItem
          icon={icons.EmailIcon}
          text="People"
          to={routes.WORKSPACE_SETTINGS_PEOPLE.create({ workspaceId })}
        />
      ) : (
        canRenderSettingsMenu && (
          <SettingsMenu
            workspace={workspace}
            permissions={settingsMenuPermissions}
          />
        )
      )}
    </>
  )
}

/**
 * BottomMenu
 *
 * Renders the bottom menu on the sidebar with
 * Discover, Settings and Help menu
 */
const BottomMenu: React.FC<BottomMenuProps> = ({ workspace }) => {
  return (
    <Wrapper data-testid="bottom-menu">
      {workspace && <DerivedMenus workspace={workspace} />}

      <HelpMenu title="Help" />
    </Wrapper>
  )
}

export default BottomMenu
